import React, { useState, useEffect } from 'react';
import { BASE_URL } from '../../config';
import useAuth from '../../hooks/useAuth';
import usePageMeta from '../../hooks/usePageMeta';
import CustomerLayout from './Layout';
import ActionDropdown from '../../components/ActionDropdown';
import ShiftDetailsModal from '../components/ShiftDetailsModal';
import { showSuccess, showError, showConfirm, showPrompt } from '../../utils/dialog';
import BackButton from "../../components/BackButton";
import DateInput from "../../components/DateInput";
import AdminFilterPanel from '../../components/AdminFilterPanel';
import { FaSlidersH } from 'react-icons/fa';

const AssignedTask = () => {
  usePageMeta("Assigned Task");
  const user = useAuth();

  const fetchTasks = async () => {
    if (!user?.accountId) return;
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/api/temp-dashboard/tasks?accountId=${user.accountId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      const txt = await response.text();
      let data;
      try { data = JSON.parse(txt); } catch (e) { data = { success: false, message: txt }; }
      if (data.success) {
        setTasks(data.data);
        setTotalTasks(data.data.length);
      } else {
        setError(data.message || 'Failed to fetch tasks');
      }
    } catch (err) {
      setError('Failed to fetch tasks. Please try again later.');
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTask = async (taskId) => {
    if (!user?.token) {
      console.error('No user authentication found');
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/shifts/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      const txt = await response.text();
      let data;
      try { data = JSON.parse(txt); } catch (e) { data = { success: false, message: txt }; }
      if (data.success) {
        setSelectedShiftData(data.data);
        setShiftModalOpen(true);
      } else {
        showError(data.message || 'Failed to load shift details');
      }
    } catch (err) {
      console.error('Error fetching task details:', err);
    }
  };

  const handleCheckIn = async (taskId) => {
    if (!user?.accountId || !user?.token) {
      console.error('No user authentication found');
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/shifts/${taskId}/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          accountId: user.accountId,
          timestamp: new Date().toISOString()
        })
      });
      const txt = await response.text();
      let data;
      try { data = JSON.parse(txt); } catch (e) { data = { success: false, message: txt }; }
      if (data.success) {
        showSuccess(data.message || 'Checked in successfully');
        await fetchTasks();
      } else {
        showError(data.message || 'Failed to check in');
      }
    } catch (err) {
      console.error('Error checking in:', err);
      showError('Network error while checking in');
    }
  };

  const handleCancel = async (taskId) => {
    if (!(await showConfirm('Are you sure you want to cancel this shift?'))) return;
    
    if (!user?.accountId || !user?.token) {
      console.error('No user authentication found');
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/shifts/${taskId}/unassign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ accountId: user.accountId })
      });
      const txt = await response.text();
      let data;
      try { data = JSON.parse(txt); } catch (e) { data = { success: false, message: txt }; }
      if (data.success) {
        showSuccess(data.message || 'Shift cancelled');
        await fetchTasks();
      } else {
        showError(data.message || 'Failed to cancel shift');
      }
    } catch (err) {
      console.error('Error cancelling shift:', err);
      showError('Network error while cancelling shift');
    }
  };

  const handleReportIssue = async (taskId) => {
    if (!user?.accountId || !user?.token) {
      console.error('No user authentication found');
      return;
    }

    try {
      const message = await showPrompt('Please describe the issue:', 'Report Issue', 'Describe the issue in detail');
      if (!message) return;

      const response = await fetch(`${BASE_URL}/api/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          object_id: taskId,
          note: message
        })
      });
      const txt = await response.text();
      let data;
      try { data = JSON.parse(txt); } catch (e) { data = { success: false, message: txt }; }
      if (data.success) {
        showSuccess('Issue reported successfully');
        await fetchTasks();
      } else {
        showError(data.message || 'Failed to report issue');
      }
    } catch (err) {
      console.error('Error reporting issue:', err);
      showError('Network error while reporting issue');
    }
  };

  const [selectedTab, setSelectedTab] = useState("TASK LIST");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("all");
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalTasks, setTotalTasks] = useState(0);
  const [error, setError] = useState(null);
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [selectedShiftData, setSelectedShiftData] = useState(null);
  const [itemsPerPage] = useState(15); // match Earnings for consistency

  useEffect(() => { fetchTasks(); }, [user?.accountId, user?.token]);
  
  // Deduplicate tasks by unique id (apply globally)
  const dedupedTasks = Array.from(new Map(tasks.map(task => [task.id, task])).values());
  
  // Use dedupedTasks for all further logic:
  const getFilteredTasks = () => {
    return dedupedTasks.filter(task => {
      if (searchTerm && !String(task.job).toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (filterBy !== 'all' && task.status !== filterBy) return false;
      // apply extra panel filters
      if (filterStatuses.length > 0 && !filterStatuses.includes(task.status)) return false;
      if (filterFromDate) {
        const d = new Date(task.date_time);
        const from = new Date(filterFromDate);
        if (isNaN(d) || d < from) return false;
      }
      if (filterToDate) {
        const d = new Date(task.date_time);
        const to = new Date(filterToDate);
        if (isNaN(d) || d > to) return false;
      }
      return true;
    });
  };

  const filteredTasks = getFilteredTasks();
  const totalItems = filteredTasks.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const currentPageSafe = Math.max(1, Math.min(currentPage, totalPages));
  const pageRows = filteredTasks.slice((currentPageSafe - 1) * itemsPerPage, currentPageSafe * itemsPerPage);

  // Pagination button logic
  const getVisiblePages = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    } else if (currentPageSafe <= 3) {
      return [1, 2, 3, '...', totalPages];
    } else if (currentPageSafe >= totalPages - 2) {
      return [1, '...', totalPages - 2, totalPages - 1, totalPages];
    } else {
      return [1, '...', currentPageSafe - 1, currentPageSafe, currentPageSafe + 1, '...', totalPages];
    }
  };
  const visiblePages = getVisiblePages();

  return (
    <CustomerLayout active="assignedTask">
      <div className="py-2 px-2 flex flex-col min-h-screen">
        {/* Header */}
        <header className="justify-between ml-3 mb-2 ml-2">
          <div className="flex items-center gap-3">
            <BackButton />
            <h1 className="text-2xl font-semibold text-foreground">Assigned Task</h1>
          </div>
        </header>

        <div className="h-[1px] bg-[#a15d33] mx-3"></div>

        <div className="mt-4 mb-2 mx-3 p-3 border border-gray-200 rounded-xl h-full flex flex-col">
          <div className="flex space-x-4 mb-4 border-b border-border">
            {["TASK LIST"].map(tab => (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab)}
                  className={`pb-2 px-1 text-sm text-[#DC1F00] font-medium border-b-2 transition-colors ${
                      selectedTab === tab
                      ? "border-[#DC1F00] text-sidebar-active"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                  >
                  {tab}
                </button>
              ))}
          </div>

          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-1.5 border border-gray-400 rounded-xl bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active w-56"
            />
            <button
              className="ml-2 p-2 hover:bg-gray-50 rounded transition-colors"
              onClick={() => setOpenFilterPanel(true)}
              title="Filter"
            >
              <FaSlidersH className="h-4 w-4 text-gray-600" />
            </button>
            </div>
          </div>

          <div className="mb-3 text-xs text-muted-foreground flex gap-6 items-center">
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap">Sorted by:</span>
              <span className="text-foreground"></span>
            </div>
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap">Filter:</span>
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded-md text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-sidebar-active/20"
              >
                <option value="all">All</option>
              </select>
            </div>
          </div> 


          {selectedTab === "TASK LIST" && (
            <table className="min-w-full border shadow">
              <thead className="bg-[#dc1f0080] text-white">
                <tr className="text-left select-none">
                  <th className="border text-sm font-medium pl-4 py-2 rounded-tl-lg w-1/5">Job</th>
                  <th className="border text-sm font-medium px-3 py-2 w-[15%]">Date & Time</th>
                  <th className="border text-sm font-medium px-3 py-2 w-[15%]">Location</th>
                  <th className="border text-sm font-medium px-3 py-2 w-[15%]">Pay Rate</th>
                  <th className="border text-sm font-medium px-3 py-2 w-[15%]">Status</th>
                  <th className="border text-sm font-medium px-3 py-2 rounded-tr-lg w-[3%]"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-4">Loading...</td></tr>
                ) : error ? (
                  <tr><td colSpan="6" className="text-center py-4 text-red-600">{error}</td></tr>
                ) : totalItems === 0 ? (
                  <tr><td colSpan="6" className="text-center py-4">No assigned tasks found</td></tr>
                ) : (
                  pageRows.map((task, index) => (
                    <tr key={`${task.id}-${index}`} className="text-left text-base hover:bg-gray-50">
                      <td className="pl-4 py-1.5 whitespace-normal break-words">{task.job}</td>
                      <td className="pl-4 py-1.5 whitespace-normal break-words">{task.date_time}</td>
                      <td className="pl-4 py-1.5 whitespace-normal break-words">{task.location}</td>
                      <td className="pl-4 py-1.5 whitespace-normal break-words">{task.pay_rate}</td>
                      <td className="pl-4 py-1.5 whitespace-normal break-words">
                        <span className={`px-2 py-1 rounded-full text-sm ${
                          task.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          task.status === 'active' ? 'bg-blue-100 text-blue-800' :
                          task.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                          task.status === 'paid'
                            ? 'text-green-600 border border-green-300 bg-green-50'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-1.5">
                        <ActionDropdown
                          actions={[
                            { label: "View Details", onClick: () => handleViewTask(task.id) },
                            task.status === 'confirmed' && { label: "Check In", onClick: () => handleCheckIn(task.id) },
                            task.status === 'confirmed' && { label: "Cancel Shift", onClick: () => handleCancel(task.id) },
                            { label: "Report Issue", onClick: () => handleReportIssue(task.id) }
                          ].filter(Boolean)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
            {shiftModalOpen && selectedShiftData && (
              <ShiftDetailsModal open={shiftModalOpen} onClose={() => setShiftModalOpen(false)} data={selectedShiftData} />
            )}
            <AdminFilterPanel
              isOpen={openFilterPanel}
              title="Task Filters"
              onClose={() => setOpenFilterPanel(false)}
              onApply={() => setOpenFilterPanel(false)}
              onClear={() => { setFilterFromDate(''); setFilterToDate(''); setFilterStatuses([]); setFilterBy('all'); }}
            >
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-[#00000080] mb-1">From</label>
                  <DateInput value={filterFromDate} onChange={setFilterFromDate} className="w-full border px-2 py-1 rounded" />
                </div>
                <div>
                  <label className="block text-sm text-[#00000080] mb-1">To</label>
                  <DateInput value={filterToDate} onChange={setFilterToDate} className="w-full border px-2 py-1 rounded" />
                </div>
                <div>
                  <label className="block text-sm text-[#00000080] mb-1">Statuses (multi)</label>
                  <div className="space-y-2">
                    {['confirmed','active','completed','paid','pending'].map(s=> (
                      <label key={s} className="flex items-center gap-2">
                        <input type="checkbox" checked={filterStatuses.includes(s)} onChange={(e)=>{
                          if (e.target.checked) setFilterStatuses(prev=>[...prev,s]); else setFilterStatuses(prev=>prev.filter(x=>x!==s));
                        }} />
                        <span className="capitalize">{s}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </AdminFilterPanel>
        </div>

        {/* Pagination controller */}
        <div className="flex items-center justify-between mt-auto text-sm text-muted-foreground">
          <span>
            {totalItems === 0
              ? 'No tasks to show'
              : `Showing ${Math.min(itemsPerPage * (currentPageSafe - 1) + 1, totalItems)}–${Math.min(itemsPerPage * currentPageSafe, totalItems)} of ${totalItems} Tasks`}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-2 border border-[#DC1F0033] text-buttonWhite rounded-md hover:bg-gray-100"
              disabled={currentPageSafe === 1}
              onClick={() => setCurrentPage(currentPageSafe - 1)}
            >
              Previous
            </button>
            {visiblePages.map((page, idx) =>
              page === '...'
                ? (<span key={idx} className="px-3 py-2">...</span>)
                : (
                  <button
                    key={idx}
                    className={`px-3 py-2 border border-[#DC1F0033] rounded-md ${currentPageSafe === page ? "bg-[#DC1F00] text-white" : "hover:bg-gray-100"}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                )
            )}
            <button
              className="px-3 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-md"
              disabled={currentPageSafe === totalPages}
              onClick={() => setCurrentPage(currentPageSafe + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default AssignedTask;
